import type { ShippingAddress } from '#types/cart'

declare global {
  interface Window {
    Klarna: Record<string, any>
  }
}

interface KlarnaAuthorizeResponse {
  payment_instrument_id: string
  sessionID: string
  clientToken: string
  at: string
}

/**
 * Formats a ShippingAddress for Klarna Payments API address fields.
 *
 * @param address - Shipping or billing address
 * @returns Klarna-formatted address object with `given_name`, `family_name`, `street_address`, etc.
 *
 * @remarks
 * - Concatenates `addressLine1` and `addressLine2` into `street_address` (space-separated, filters out empty values)
 * - `title` is always empty string (not used by Klarna)
 */
const getAddressField = (address: ShippingAddress) => ({
  given_name: address.firstName,
  family_name: address.lastName,
  email: address.email,
  title: '',
  street_address: [address.addressLine1, address.addressLine2].filter(Boolean).join(' '),
  postal_code: address.postalCode,
  city: address.city,
  region: address.province,
  phone: address.phone,
  country: address.country
})

/**
 * Integrates Klarna Payments widget for checkout payment authorization.
 *
 * Handles:
 * - Klarna SDK loading from CDN (https://x.klarnacdn.net/kp/lib/v1/api.js)
 * - Payment session creation (via Cart API) with provider detection (Cybersource or Adyen)
 * - Payment instrument addition to basket
 * - Klarna widget rendering in checkout UI
 * - Payment authorization with billing/shipping address (for pickup/STS orders only)
 * - Special handling: BOPIS/STS orders skip billing address addition to avoid payment instrument conflicts
 *
 * @param billingAddress - Optional billing address (reactive or plain); used only for Adyen provider and non-pickup/STS orders
 * @returns Klarna Payments lifecycle methods: `init`, `load`, and `authorize`
 *
 * @example
 * ```vue
 * <script setup>
 * const billingAddress = ref<ShippingAddress>({ ... })
 * const { init, load, authorize } = useKlarna(billingAddress)
 *
 * // 1. Initialize (on checkout mount)
 * await init(cart.basketId, true)
 *
 * // 2. Load widget into container
 * await load('#klarna-container')
 *
 * // 3. Authorize payment (on submit)
 * const { at, payment_instrument_id } = await authorize(billingAddress.value, shippingAddress.value)
 * </script>
 * ```
 *
 * @remarks
 * - **Browser-only**: Klarna SDK is loaded from CDN on first `init()` call
 * - **Provider detection**: Cart API returns `CYBERSOURCE` or `ADYEN` in payment session response
 * - **BOPIS/STS handling**: For pickup or ship-to-store orders, billing address is NOT added to cart before authorization (prevents payment instrument conflicts)
 * - **State persistence**: Uses `useState` to persist `config`, `inited`, and `provider` across component remounts
 * - **Authorization flow**: If user closes Klarna modal, promise is silently rejected (no error). If Klarna returns error, throws `Error('Error authorizing Klarna')`
 * - **Klarna widget config**: Uses first payment method category from payment instrument response
 */
export default (billingAddress?: MaybeRefOrGetter<ShippingAddress>) => {
  const { load: loadScript } = useScript('https://x.klarnacdn.net/kp/lib/v1/api.js', { trigger: 'manual' })
  const cart = useCartStore()
  const config = useState<Record<string, any>>('klarnaConfig', () => ({}))
  const inited = useState<boolean>('klarnaInited', () => false)
  const provider = useState<'CYBERSOURCE' | 'ADYEN' | undefined>('klarnaProvider')

  /**
   * Initializes Klarna SDK, creates payment session, and adds Klarna payment instrument to basket.
   *
   * On first call, loads Klarna SDK from CDN. Fetches payment session from Cart API (detects provider: Cybersource or Adyen).
   * For Adyen + non-pickup/STS orders, optionally saves billing address to cart. Adds KLARNA payment instrument to basket
   * and initializes `window.Klarna.Payments` with returned config.
   *
   * @param basketId - Basket ID (cart ID) to add payment instrument to
   * @param saveBillingAddress - If true, saves billing address to cart (only for Adyen provider and non-pickup/STS orders)
   *
   * @example
   * ```ts
   * await init(cart.basketId, true)
   * ```
   *
   * @throws Payment session error if Cart API returns `success: false`
   *
   * @remarks
   * - **SDK loading**: Only loads Klarna SDK script once per session (tracked via `inited` state)
   * - **Provider detection**: Sets `provider` state to `CYBERSOURCE` or `ADYEN` based on Cart API response
   * - **Billing address caveat**: BOPIS/STS orders skip billing address addition to prevent payment instrument conflicts
   * - **Payment instrument config**: Stores Klarna payment instrument config in `config` state (used by `load` and `authorize`)
   * - **API calls sequence**: 1) `getPaymentSession` → 2) optional `addBillingAddress` → 3) `addPaymentInstrument` → 4) `Klarna.Payments.init`
   */
  const init = async (basketId: string, saveBillingAddress?: boolean) => {
    if (!inited.value) {
      await loadScript()
      inited.value = true
    }

    const _billingAddress = toValue(billingAddress)

    const session = await useApi().cart.$getPaymentSession({
      referer: window.location.origin,
      paymentMethodId: 'CREDIT_CARD',
      paymentMethod: 'klarna'
    }, {
      headers: {
        'content-type': 'application/json'
      }
    })

    if (!session.success) throw session

    provider.value = session.provider

    // if is pickup or sts don't run billing address as the payment instrument will have wrong billing address
    if (saveBillingAddress && _billingAddress && provider.value === 'ADYEN' && !isPickupOrSts(cart.shippingMethods[0].code)) {
      await cart.addBillingAddress(_billingAddress)
    }

    const { payment_instruments } = await useApi().cart.$addPaymentInstrument(basketId, {
      payment_method_id: 'KLARNA'
    })

    config.value = payment_instruments
      .find(({ payment_method_id }) => payment_method_id === 'KLARNA')

    window.Klarna.Payments.init(config.value)
  }

  /**
   * Loads Klarna payment widget into a DOM container.
   *
   * @param container - CSS selector string (e.g., '#klarna-container') or HTMLElement reference
   *
   * @example
   * ```ts
   * await load('#klarna-payment-widget')
   * ```
   *
   * @remarks
   * - Must be called after `init()`
   * - Uses payment method category from `config` state (set during `init`)
   * - Klarna SDK renders payment method selection UI (installments, pay-in-3, etc.) into the container
   */
  const load = (container: string | HTMLElement) => window.Klarna.Payments.load({
    container,
    payment_method_category: config.value?.payment_method_categories?.[0]?.identifier
  })

  /**
   * Authorizes Klarna payment and returns payment instrument details for order placement.
   *
   * Shows Klarna authorization modal to user. If approved, resolves with `payment_instrument_id`, `sessionID`, `clientToken`, and authorization token (`at`).
   * If user closes modal without approving, promise is silently rejected (no error). If Klarna returns authorization error, throws error.
   *
   * @param billingAddress - Billing address (required for pickup/STS orders)
   * @param shippingAddress - Shipping address (required for pickup/STS orders)
   * @returns Promise resolving to payment instrument details (includes authorization token `at` for order submission)
   *
   * @example
   * ```ts
   * try {
   *   const { at, payment_instrument_id } = await authorize(billing, shipping)
   *   await submitOrder({ authorizationToken: at })
   * } catch (err) {
   *   if (err) console.error('Klarna authorization failed')
   *   // Silently rejected = user closed modal
   * }
   * ```
   *
   * @throws `Error('Error authorizing Klarna')` if Klarna returns authorization error
   * @throws Silently rejected (no error) if user closes Klarna modal without approving
   *
   * @remarks
   * - **BOPIS/STS orders**: Billing and shipping addresses are passed to Klarna API (required by Klarna for these order types)
   * - **Non-pickup orders**: Empty object `{}` is passed (billing address already set in cart during `init`)
   * - **Modal close detection**: If `klarnaResponse.show_form` is true, user closed modal → silent reject
   * - **Klarna SDK callback**: `Klarna.Payments.authorize` is callback-based; wrapped in Promise for async/await compatibility
   */
  const authorize
    = (billingAddress: ShippingAddress, shippingAddress: ShippingAddress): Promise<KlarnaAuthorizeResponse> =>
      new Promise((resolve, reject) => window.Klarna.Payments.authorize(
        { payment_method_category: config.value?.payment_method_categories?.[0]?.identifier },
        isPickupOrSts(cart.shippingMethods[0].code)
          ? {
              billing_address: getAddressField(billingAddress),
              shipping_address: getAddressField(shippingAddress),
              attachment: config.value.attachment
            }
          : {},
        (klarnaResponse) => {
          if (klarnaResponse.approved) {
            resolve({
              payment_instrument_id: config.value.payment_instrument_id,
              sessionID: config.value.session_id,
              clientToken: config.value.client_token,
              at: klarnaResponse.authorization_token
            })
          }
          else {
          // if klarnaResponse.approved is false, it means the user has closed the Klarna modal or there was an error authorizing Klarna.
          // https://docs.klarna.com/klarna-payments/additional-resources/klarna-payments-sdk-reference/#authorizecallback
            if (klarnaResponse.show_form) {
            // If show_form is true, it means the user has closed the Klarna modal. Silently reject.
            // eslint-disable-next-line prefer-promise-reject-errors
              reject()
            }
            else {
            // Actual error authorizing Klarna. Throw an error.
              reject(new Error('Error authorizing Klarna'))
            }
          }
        }
      ))

  return {
    load,
    init,
    authorize,
  }
}
